import { Bell, CheckCheck, Settings as SettingsIcon } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { AppNotification } from '../types';
import { useMarkAllNotificationsRead, useMarkNotificationRead, useNotifications } from '../queries';
import { useNotificationsChannel } from '../useNotificationsChannel';

/** The link as an in-app path, or null when it would leave the app. Resolved
 *  the way the browser will (`/\\host`, tabs and `//host` all mean another
 *  origin), so the check is on the origin, not on how the string starts. */
function appLink(n: AppNotification): string | null {
  if (!n.link) return null;
  try {
    const url = new URL(n.link, window.location.origin);
    // `/.//host` passes the origin check but leaves a `//host` path behind.
    if (url.origin !== window.location.origin || url.pathname.startsWith('//')) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}

/** Recent notifications the panel shows. */
const PANEL_SIZE = 8;

/** Header bell: unread badge + a popover of recent notifications. A row with a
 *  link opens it (e.g. the work item at the comment); others open in a dialog. */
export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // The notification opened for full reading (the panel truncates each row).
  const [selected, setSelected] = useState<AppNotification | null>(null);
  // Recent items for the panel; a separate count query for an accurate badge
  // (the list `count` is total, not unread), each fetching only what it shows.
  // A new notification arrives over the realtime channel; both lists also
  // poll — slower while the channel is live — the fallback when it can't.
  const live = useNotificationsChannel();
  const recent = useNotifications({ page_size: PANEL_SIZE }, { poll: true, live });
  const unread = useNotifications({ is_read: false, page_size: 1 }, { poll: true, live });
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const items = recent.data?.results ?? [];
  const unreadCount = unread.data?.count ?? 0;

  // Set while following a link, so closing the popover doesn't hand focus
  // back to the bell — the linked comment takes it (CommentsPanel).
  const followingLink = useRef(false);

  function openNotification(n: AppNotification) {
    if (!n.is_read) markRead.mutate(n.id);
    const link = appLink(n);
    followingLink.current = link !== null;
    setOpen(false); // close the popover so the target (or dialog) is the focus
    if (link) navigate(link);
    else setSelected(n);
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" aria-label="Notifications">
            <Bell />
            {unreadCount > 0 ? (
              <span
                className="bg-primary text-primary-foreground absolute -top-0.5 -right-0.5 grid min-w-4 place-items-center rounded-full px-1 text-[10px] leading-4 font-semibold"
                aria-label={`${unreadCount} unread`}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            ) : null}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-80 p-0"
          onCloseAutoFocus={(e) => {
            if (!followingLink.current) return;
            followingLink.current = false;
            e.preventDefault();
          }}
        >
          <div className="flex items-center justify-between border-b px-3 py-2">
            <span className="text-sm font-semibold">Notifications</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => markAll.mutate()}
              disabled={unreadCount === 0 || markAll.isPending}
            >
              <CheckCheck className="size-3.5" /> Mark all read
            </Button>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {recent.isPending ? (
              <p className="text-muted-foreground px-3 py-6 text-center text-sm">Loading…</p>
            ) : items.length === 0 ? (
              <div className="text-muted-foreground flex flex-col items-center gap-2 px-3 py-8 text-sm">
                <Bell className="size-5" />
                You&rsquo;re all caught up.
              </div>
            ) : (
              <ul className="divide-y">
                {items.map(n => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => openNotification(n)}
                      className={cn(
                        'hover:bg-accent/50 flex w-full items-start gap-2 px-3 py-2.5 text-left',
                        !n.is_read && 'bg-muted/40'
                      )}
                    >
                      <span
                        className={cn(
                          'mt-1.5 size-2 shrink-0 rounded-full',
                          n.is_read ? 'bg-transparent' : 'bg-primary'
                        )}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{n.title || n.verb}</span>
                        {n.context ? (
                          <span className="text-foreground/80 block truncate text-xs">{n.context}</span>
                        ) : null}
                        {n.message ? (
                          <span className="text-muted-foreground line-clamp-2 text-xs">
                            {n.message}
                          </span>
                        ) : null}
                        <span className="text-muted-foreground mt-0.5 block text-[11px]">
                          {new Date(n.created).toLocaleString()}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-end border-t px-3 py-2">
            <Button asChild variant="link" size="sm" className="h-7 gap-1.5 px-0 text-xs">
              <Link to="/settings/notifications/preferences" onClick={() => setOpen(false)}>
                <SettingsIcon className="size-3.5" /> Notification preferences
              </Link>
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Dialog open={selected !== null} onOpenChange={o => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          {selected ? (
            <>
              <DialogHeader>
                <DialogTitle>{selected.title || selected.verb}</DialogTitle>
                <DialogDescription className="text-[11px]">
                  {new Date(selected.created).toLocaleString()}
                </DialogDescription>
              </DialogHeader>
              {selected.context ? <p className="text-sm font-medium">{selected.context}</p> : null}
              <p className="text-sm whitespace-pre-line">{selected.message || 'No details.'}</p>
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
